/**
 * @typedef {{
 *   requestUpdate: () => void,
 *   getUpdateComplete: () => Promise<boolean>,
 *   isUpdatePending: () => boolean
 * }} RenderScheduler
 */

/** @typedef {() => void} RenderFlush */

/**
 * Creates a Lit-style async render scheduler around a render flush function.
 *
 * Instead of using setTimeout(fn, 0) which creates timer-fired tasks that show
 * up as long callbacks in performance traces, this scheduler uses promise
 * chaining to run renders on microtasks. Microtasks execute at the end of the
 * current JavaScript turn, before the browser yields to paint or input — so
 * renders still happen promptly, but without the scheduling jitter and priority
 * inversion that come with macrotask timers.
 *
 * The scheduler coalesces multiple requestUpdate() calls within the same JS
 * turn into a single render, matching Lit's batching behavior.
 *
 * @param {RenderFlush} flushRender - The function that performs the actual DOM
 *   render. Passed in (rather than imported) to avoid circular dependencies
 *   between the scheduler module and the app module that defines the render.
 * @returns {RenderScheduler}
 */
export function createRenderScheduler(flushRender) {
  if (typeof flushRender !== "function") {
    throw new Error("createRenderScheduler requires a render flush function.");
  }

  // True from the moment requestUpdate() is called until the queued render
  // fully settles (including any follow-up pass). Used to deduplicate requests:
  // if this is already true and we're not mid-flush, a new requestUpdate() is a
  // no-op because the pending render will pick up whatever state changed.
  let isUpdatePendingValue = false;

  // True only while flushRender() is synchronously executing on the call stack.
  // Distinguishes "update is queued but hasn't started" (where we can fold new
  // requests into the existing queue entry) from "render is running right now"
  // (where a new request means we need a follow-up pass after this one).
  let isFlushing = false;

  // Set to true when requestUpdate() is called while isFlushing is true.
  // After the current flush finishes, the scheduler checks this flag and, if
  // set, schedules exactly one more render pass. Multiple mid-flush requests
  // all collapse into this single boolean — no unbounded queue growth.
  let needsAnotherPass = false;

  // The promise that represents the outcome of the current (or most recent)
  // render cycle. Callers can await this to know when the DOM is up to date.
  //
  // Resolves to true  → render completed, no follow-up was needed.
  // Resolves to false → render completed but a follow-up pass was scheduled
  //                      (caller should await the next getUpdateComplete() if
  //                      they need full quiescence).
  // Rejects           → flushRender() threw an error.
  //
  // Initialized to a resolved promise so the very first enqueueUpdate() has
  // something to await (its microtask boundary).
  /** @type {Promise<boolean>} */
  let updateComplete = Promise.resolve(true);

  /**
   * Returns whether a render is currently queued or in progress.
   * Useful for callers that want to check synchronously without awaiting.
   * @returns {boolean}
   */
  function isUpdatePending() {
    return isUpdatePendingValue;
  }

  /**
   * Returns the promise for the current render cycle. If no render is pending,
   * this is the promise from the last completed render (already resolved).
   * @returns {Promise<boolean>}
   */
  function getUpdateComplete() {
    return updateComplete;
  }

  /**
   * Requests an async render.
   *
   * Three cases:
   *
   * 1. An update is pending AND we're currently inside flushRender():
   *    Set needsAnotherPass so the scheduler queues one follow-up render after
   *    the current flush finishes. This handles re-entrant updates — e.g. a
   *    render callback that detects it needs to trigger another state change.
   *
   * 2. An update is pending but we're NOT flushing (the render is queued on a
   *    microtask but hasn't started yet): Do nothing. The queued render will
   *    pick up whatever state changes prompted this call, because state is read
   *    at flush time, not at request time.
   *
   * 3. No update is pending: Capture the current updateComplete as
   *    previousUpdate, flip the pending flag, and chain a new enqueueUpdate()
   *    onto it. This creates the microtask boundary that defers the render.
   *
   * @returns {void}
   */
  function requestUpdate() {
    // Case 1: mid-flush re-entrant request → just flag for a follow-up pass
    if (isUpdatePendingValue && isFlushing) {
      needsAnotherPass = true;
      return;
    }

    // Case 2: already queued, not yet flushing → the pending render covers it
    if (isUpdatePendingValue) {
      return;
    }

    // Case 3: no update pending → schedule one via promise chaining
    const previousUpdate = updateComplete;
    isUpdatePendingValue = true;
    // Reset needsAnotherPass here in case a stale value lingered from a
    // previous cycle (shouldn't happen, but defensive).
    needsAnotherPass = false;
    // enqueueUpdate returns a new promise that becomes the new updateComplete.
    // Because enqueueUpdate is async and awaits previousUpdate, the actual
    // flushRender() call is deferred to a microtask — not run synchronously.
    updateComplete = enqueueUpdate(previousUpdate);
  }

  /**
   * The core of the scheduler. This async function:
   *
   * 1. Awaits the previous update's promise. This is the microtask boundary —
   *    it's what causes the render to be deferred rather than running
   *    synchronously inside requestUpdate(). All synchronous requestUpdate()
   *    calls in the same JS turn share the same microtask boundary, so they
   *    collapse into one render.
   *
   * 2. Calls flushRender() to perform the actual DOM update.
   *
   * 3. Checks if any mid-flush re-entrant requests set needsAnotherPass, and
   *    if so, schedules exactly one follow-up render via requestUpdate().
   *
   * 4. Returns true if the render completed cleanly with no follow-up needed,
   *    false if a follow-up was scheduled, or throws if flushRender() threw.
   *
   * @param {Promise<boolean>} previousUpdate - The updateComplete promise from
   *   the prior render cycle. Awaiting it serializes renders and creates the
   *   microtask deferral.
   * @returns {Promise<boolean>}
   */
  async function enqueueUpdate(previousUpdate) {
    // Wait for the previous render to finish. This is the microtask boundary
    // that batches all same-turn requestUpdate() calls into one flush.
    try {
      await previousUpdate;
    } catch (error) {
      // The previous render threw. We refire the error asynchronously so it
      // still surfaces as an unhandled rejection, but we don't let it block
      // this render — otherwise one bad render would wedge the entire
      // scheduler and no future renders could ever run.
      Promise.reject(error);
    }

    // Track whether this render completes cleanly. Starts optimistic.
    let completed = true;
    // Error tracking: we catch flushRender errors so we can clean up state
    // before re-throwing. Without this, a throw inside flushRender would leave
    // isFlushing=true and isUpdatePendingValue=true permanently, wedging the
    // scheduler.
    let didThrow = false;
    /** @type {unknown} */
    let thrownError = undefined;

    // Enter the flushing state. While this flag is true, any requestUpdate()
    // call sets needsAnotherPass instead of trying to enqueue a new render.
    isFlushing = true;
    // Clear needsAnotherPass right before flushing so we only detect requests
    // that happen during this specific flush, not leftovers from prior cycles.
    needsAnotherPass = false;

    try {
      // This is the actual DOM render. It runs synchronously — when it returns,
      // the DOM is updated. If the render function reads app state and calls
      // requestUpdate() internally (re-entrant), needsAnotherPass gets set.
      flushRender();
    } catch (error) {
      didThrow = true;
      thrownError = error;
    }

    // Exit the flushing state. These flags must be cleared even if flushRender
    // threw, so the scheduler remains usable for future renders.
    isFlushing = false;
    isUpdatePendingValue = false;

    // If any code called requestUpdate() while we were flushing, schedule
    // exactly one follow-up render. We go through requestUpdate() so it sets
    // up all the state correctly (isUpdatePendingValue, updateComplete, etc).
    // The current promise resolves to false to tell awaiting callers that the
    // render cycle isn't fully settled yet.
    if (needsAnotherPass) {
      completed = false;
      needsAnotherPass = false;
      requestUpdate();
    }

    // Re-throw after state cleanup so the promise rejects and callers awaiting
    // updateComplete see the error. State is already cleaned up above, so the
    // scheduler is still usable even after this throw.
    if (didThrow) {
      throw thrownError;
    }

    return completed;
  }

  return {
    requestUpdate,
    getUpdateComplete,
    isUpdatePending,
  };
}
